import { QuillModules } from 'ngx-quill';
import type Quill from 'quill';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  concatMap,
  from,
  merge,
  takeUntil,
} from 'rxjs';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewEncapsulation,
  inject,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { DialogService } from '../../services';

export type EditorImageUploader = (file: File) => Observable<string>;

export type EditorImageUrlValidator = (url: string) => boolean;

const IMAGE_URL_ICON = `<svg viewBox="0 0 18 18">
  <rect class="ql-stroke" height="8" width="10" x="1" y="2"></rect>
  <circle class="ql-fill" cx="4" cy="5" r="1"></circle>
  <polyline class="ql-even ql-fill" points="3 9 3 8.5 5 6.5 6 7.5 8 5.5 9 6.5 9 9 3 9"></polyline>
  <path class="ql-stroke" d="M11.5,12.5l2-2a1.5,1.5,0,0,1,2,2l-2,2a1.5,1.5,0,0,1-2,0"></path>
  <path class="ql-stroke" d="M13.5,13.5l-2,2a1.5,1.5,0,0,1-2-2l2-2a1.5,1.5,0,0,1,2,0"></path>
</svg>`;

const IMAGE_MIMETYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

@Component({
  selector: 'phoenix-editor',
  standalone: false,
  template: `<div fxFlex="100">
      @if (isLayoutReady) {
        @if (label) {
          <div class="mb-1">
            <strong id="title" class="text-base theme-text-1">{{
              label
            }}</strong>
          </div>
        }
        <quill-editor
          [formControl]="control"
          [modules]="quillModules"
          (onEditorCreated)="onEditorCreated($event)"
          (onContentChanged)="onContentChanged($event)"
          style="width: 100%"
          [attr.aria-label]="ariaLabel"
        ></quill-editor>
      }
    </div>

    <ng-template #imageUrlDialog>
      <mat-form-field class="w-full">
        <mat-label>Image URL</mat-label>
        <input
          matInput
          [formControl]="imageUrlControl"
          [attr.aria-label]="'editorImageUrl'"
        />
        @if (imageUrlControl.hasError('imageUrl')) {
          <mat-error>This image URL is not allowed</mat-error>
        }
      </mat-form-field>
    </ng-template> `,
  styleUrls: ['./editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EditorComponent implements OnInit, OnDestroy {
  @Input() ariaLabel = '';
  @Input() allowImg = false;
  @Input() control = new FormControl();
  @Input() label = '';
  @Input() imageUploader?: EditorImageUploader;
  @Input() imageUrlValidator?: EditorImageUrlValidator;
  @Output() valueChange = new EventEmitter<string>();

  imageUrlDialog = viewChild<TemplateRef<unknown>>('imageUrlDialog');

  public isLayoutReady = false;
  public quillModules!: QuillModules;

  readonly imageUrlControl = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      (control: AbstractControl<string>): ValidationErrors | null =>
        !control.value || this.imageUrlValidator?.(control.value.trim())
          ? null
          : { imageUrl: true },
    ],
  });

  private readonly dialogService = inject(DialogService);
  private quill?: Quill;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Define Quill modules and options
    const attachmentRow = this.uploadEnabled
      ? [
          'link',
          'image',
          ...(this.imageUrlEnabled ? ['imageUrl'] : []),
          'video',
        ]
      : ['link'];

    this.quillModules = {
      toolbar: {
        container: [
          [{ align: [] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'bullet' }, { list: 'ordered' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['code-block', 'blockquote'],
          attachmentRow,
          [{ script: 'sub' }, { script: 'super' }],
          [{ direction: 'rtl' }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ color: [] }, { background: [] }],
          [{ font: [] }],
          ['clean'],
        ],
        ...(this.uploadEnabled
          ? {
              handlers: {
                image: () => this.promptForImage(),
                ...(this.imageUrlEnabled
                  ? { imageUrl: () => this.promptForImageUrl() }
                  : {}),
              },
            }
          : {}),
      },
      clipboard: {
        matchVisual: false,
      },
      uploader: this.uploadEnabled
        ? {
            mimetypes: IMAGE_MIMETYPES,
            handler: (range: { index: number } | null, files: File[]) =>
              this.uploadAndInsert(files, range?.index),
          }
        : { mimetypes: [], handler: () => undefined },
    };

    this.isLayoutReady = true;
    this.changeDetector.detectChanges();
  }

  public onEditorCreated(quill: Quill): void {
    this.quill = quill;

    const toolbar = quill.getModule('toolbar') as
      { container?: HTMLElement | null } | undefined;
    const imageUrlButton =
      toolbar?.container?.querySelector<HTMLButtonElement>('.ql-imageUrl');

    if (imageUrlButton) {
      imageUrlButton.innerHTML = IMAGE_URL_ICON;
      imageUrlButton.title = 'Insert image from URL';
      imageUrlButton.setAttribute('aria-label', 'editorInsertImageUrl');
    }
  }

  public onContentChanged(event: any): void {
    if (event?.html) {
      this.valueChange.emit(event.html);
    } else {
      this.valueChange.emit('');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get uploadEnabled(): boolean {
    return this.allowImg && !!this.imageUploader;
  }

  private get imageUrlEnabled(): boolean {
    return this.uploadEnabled && !!this.imageUrlValidator;
  }

  private promptForImageUrl(): void {
    const template = this.imageUrlDialog();

    if (this.control.disabled || !this.quill || !template) {
      return;
    }

    const index = this.quill.getSelection(true)?.index;
    this.imageUrlControl.reset();

    const dialogRef = this.dialogService.form(template, this.imageUrlControl, {
      data: {
        title: 'Insert image from URL',
        btnLabel: 'Insert',
        autoSaveOff: true,
      },
      width: '500px',
    });

    dialogRef.componentInstance.submitButton
      .pipe(takeUntil(merge(this.destroy$, dialogRef.afterClosed())))
      .subscribe(() => {
        if (this.imageUrlControl.invalid) {
          return;
        }

        this.insertImage(this.imageUrlControl.value.trim(), index);
        dialogRef.close();
      });
  }

  private insertImage(url: string, index?: number): void {
    const quill = this.quill;

    if (!quill || this.control.disabled) {
      return;
    }

    const at = index ?? quill.getLength();
    quill.insertEmbed(at, 'image', url, 'user');
    quill.setSelection(at + 1, 0, 'silent');
  }

  private promptForImage(): void {
    if (this.control.disabled) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        this.uploadAndInsert([file], this.quill?.getSelection(true)?.index);
      }
    };

    input.click();
  }

  private uploadAndInsert(files: File[], index?: number): void {
    const quill = this.quill;
    const uploader = this.imageUploader;

    if (
      !quill ||
      !uploader ||
      !this.uploadEnabled ||
      !files.length ||
      this.control.disabled
    ) {
      return;
    }

    let at = index ?? quill.getLength();

    from(files)
      .pipe(
        concatMap(file => uploader(file).pipe(catchError(() => EMPTY))),
        takeUntil(this.destroy$)
      )
      .subscribe(url => {
        if (!url) {
          return;
        }

        quill.insertEmbed(at, 'image', url, 'user');
        at += 1;
        quill.setSelection(at, 0, 'silent');
      });
  }
}
